import React, { useContext } from 'react';
import {
  mergeClasses,
  Text,
  Title3,
} from '@fluentui/react-components';
import { MSALogo } from '@adsui/assets';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useResponsiveSize } from '@adscore/with-responsive-size';
import { InvitationMeControl } from './invitation-me-control';
import { useAcceptInvitationStyles } from './styles';

export type InvitationShellProps = {
  children: React.ReactNode;
  footer?: React.ReactNode;
  // The signed-in identity shown in the top bar. Omitting either prop hides the control, so screens
  // reached before the identity is known render the bare logo top bar exactly as before.
  userName?: string;
  onSignOut?: () => void;
};

export const InvitationShell: React.FC<InvitationShellProps> = ({
  children, footer, userName, onSignOut,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useAcceptInvitationStyles();
  // Fix for ADO 10941326 (MAS 1.4.10 Reflow): drive the stacked/narrow layout off the shared
  // responsive breakpoints (@bingads-webui-react/with-responsive-size) instead of a hard-coded
  // media query, so it tracks the org-wide breakpoint definitions. 's'/'m' = viewport < 640px,
  // which covers the 320px reflow target.
  const displayMode = useResponsiveSize();
  const isNarrow = displayMode === 's' || displayMode === 'm';

  return (
    <div className={classes.root}>
      <div className={classes.topBar}>
        <MSALogo role="img" aria-label={i18n.getString(_TL_('Microsoft Advertising'))} className={mergeClasses(classes.brandLogo, classes.topBarLogo)} />
        {userName && onSignOut && <InvitationMeControl userName={userName} onSignOut={onSignOut} />}
      </div>

      <div className={classes.pageHeader}>
        <Title3 as="h1">{i18n.getString(_TL_('Create your user profile'))}</Title3>
      </div>

      <div className={mergeClasses(classes.layout, isNarrow && classes.layoutNarrow)}>
        <div className={mergeClasses(classes.rail, isNarrow && classes.railNarrow)}>
          <div className={classes.railStep}>
            <span className={classes.railDot} />
            <Text size={300}>{i18n.getString(_TL_('Create profile'))}</Text>
          </div>
        </div>
        <div className={classes.content}>{children}</div>
      </div>

      {footer && <div className={mergeClasses(classes.footer, isNarrow && classes.footerNarrow)}>{footer}</div>}
    </div>
  );
};
